import { toastError } from '@onestep/coreui/Toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useParams } from 'react-router';

import useMixpanel from '../../../../../hooks/useMixpanel';
import type { ICareLocation, INewCareLocation } from '../../types';

export default function useCareLocationService() {
  const queryClient = useQueryClient();
  const { patientUuid = '' } = useParams<{ patientUuid: string }>();
  const { trackFail } = useMixpanel();

  const moveAction = useMutation({
    mutationFn: async ({
      data,
      inValidateQuery,
    }: {
      data: INewCareLocation;
      inValidateQuery?: boolean;
    }) => {
      await axios.post(
        `/api/camel/patient_profile/administration/${patientUuid}/care_location/move`,
        data,
      );
      if (inValidateQuery) {
        invalidateAdministrationQuery();
      }
    },
  });

  const editAction = useMutation({
    mutationFn: async (data: Partial<ICareLocation>) => {
      try {
        await axios.put(
          `/api/camel/patient_profile/administration/care_location/${data.uuid}`,
          data,
        );
        invalidateAdministrationQuery();
      } catch (err) {
        trackFail('edit_care_location', { err });
        toastError({ content: 'Failed to edit care location!' });
      }
    },
  });

  const deleteAction = useMutation({
    mutationFn: async (locationId: string) => {
      try {
        await axios.delete(`/api/camel/patient_profile/administration/care_location/${locationId}`);
        invalidateAdministrationQuery();
      } catch (err) {
        trackFail('delete_care_location', { err });
        toastError({ content: 'Failed to delete care location!' });
      }
    },
  });

  const invalidateAdministrationQuery = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['camel', 'patient_profile', 'administration', patientUuid],
    });
  };

  return {
    moveCareLocation: moveAction.mutateAsync,
    editCareLocation: editAction.mutateAsync,
    deleteCareLocation: deleteAction.mutateAsync,
  };
}
